import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { collection, doc, getDoc, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import NewsImageUploader from '../components/NewsImageUploader';
import { db, storage } from '../firebase';
import { News } from '../types/News';
import { useAuth } from '../contexts/AuthContext';
import { MEMBER_TYPES } from '../types/User';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './NewsEditor.css';
import ResponsiveTitle from '../components/ResponsiveTitle';

const titleModules = { toolbar: [['bold', 'italic', 'link'], [{ 'color': [] }], ['clean']] };
const titleFormats = ['bold', 'italic', 'link', 'color'];

const contentModules = { toolbar: [[{ 'header': [1, 2, 3, false] }], ['bold', 'italic', 'underline', 'strike', 'blockquote'], [{ 'list': 'ordered' }, { 'list': 'bullet' }], [{ 'color': [] }, { 'background': [] }], ['link', 'image'], ['clean']] };
const contentFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'color', 'background', 'link', 'image'];

// Helper to extract storage path from Firebase Download URL
const getStoragePathFromUrl = (url: string): string | null => {
  try {
    if (!url.includes('firebasestorage.googleapis.com')) return null;

    // Pattern: .../o/<path>?...
    const matches = url.match(/\/o\/(.+?)(\?|$)/);
    if (matches && matches[1]) {
      return decodeURIComponent(matches[1]);
    }
    return null;
  } catch (e) {
    console.error("Error parsing storage URL", e);
    return null;
  }
};

const NewsEditor = () => {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null | undefined>(null);
  const [imageStoragePath, setImageStoragePath] = useState<string | null | undefined>(null);
  const [imageFileName, setImageFileName] = useState<string | null | undefined>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteButtonEnabled, setDeleteButtonEnabled] = useState(false);

  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    if (!user || user.role !== MEMBER_TYPES.ADMIN) {
      navigate('/');
    }
  }, [user, navigate]);

  // Charger les données de l'actualité si en mode édition
  useEffect(() => {
    const fetchNews = async () => {
      if (!isEditMode) return;

      try {
        setLoading(true);
        const newsDoc = await getDoc(doc(db, 'news', id));

        if (newsDoc.exists()) {
          const newsData = newsDoc.data() as News;
          setTitle(newsData.title);
          setContent(newsData.content);
          setImageUrl(newsData.imageUrl);
          setImageStoragePath(newsData.imageStoragePath);
          setImageFileName(newsData.imageFileName);
          // setIsPublished a été supprimé, toutes les actualités sont publiées par défaut
        } else {
          setError("Cette actualité n'existe pas.");
          setTimeout(() => navigate('/'), 2000);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération de l'actualité:", error);
        setError("Erreur lors du chargement de l'actualité.");
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [id, isEditMode, navigate]);

  // Vérifier si le texte de confirmation est correct
  useEffect(() => {
    setDeleteButtonEnabled(deleteConfirmation.toLowerCase() === 'supprimer');
  }, [deleteConfirmation]);

  const handleDelete = async () => {
    if (!isEditMode || !id) return;

    // Vérification supplémentaire que le texte de confirmation est correct
    if (deleteConfirmation.toLowerCase() !== 'supprimer') {
      return;
    }

    try {
      setDeleting(true);

      // Supprimer l'image associée si elle existe
      if (imageStoragePath) {
        try {
          const imageRef = ref(storage, imageStoragePath);
          await deleteObject(imageRef);
          console.log(`Image supprimée: ${imageStoragePath}`);
        } catch (imageError) {
          console.error("Erreur lors de la suppression de l'image:", imageError);
          // Continuer malgré l'erreur de suppression d'image
        }
      }

      // Supprimer l'actualité
      await deleteDoc(doc(db, 'news', id));
      setSuccess("Actualité supprimée avec succès!");
      setOpenDeleteDialog(false);

      // Rediriger vers la page d'accueil après un court délai
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      console.error("Erreur lors de la suppression de l'actualité:", error);
      setError("Une erreur est survenue lors de la suppression.");
      setOpenDeleteDialog(false);
    } finally {
      setDeleting(false);
      setDeleteConfirmation('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      setError("Veuillez remplir tous les champs.");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Créer l'objet de base pour les données
      const newsData: Partial<News> = {
        title: title.trim(),
        content: content.trim(),
        isPublished: true, // Toujours publié immédiatement
      };

      // Ajouter les champs d'image seulement s'ils ont une valeur (pas undefined)
      if (imageUrl) newsData.imageUrl = imageUrl;
      if (imageStoragePath) newsData.imageStoragePath = imageStoragePath;
      if (imageFileName) newsData.imageFileName = imageFileName;

      if (isEditMode) {
        // Mise à jour d'une actualité existante
        if (!imageUrl) {
          // Si l'image a été supprimée, définir explicitement les champs à null
          // car Firestore n'accepte pas undefined
          newsData.imageUrl = null;
          newsData.imageStoragePath = null;
          newsData.imageFileName = null;
        }

        await updateDoc(doc(db, 'news', id), newsData);
        setSuccess("Actualité mise à jour avec succès!");
      } else {
        // Créer une nouvelle actualité
        const newNews: News = {
          title: title.trim(),
          content: content.trim(),
          publishDate: new Date().toISOString(),
          author: user?.nickname || user?.displayName || user?.email || "Admin", // Utiliser le nickname ou le nom d'affichage de l'utilisateur
          authorId: user?.uid || "",
          isPublished: true,
          imageUrl,
          imageStoragePath,
          imageFileName,
        };

        console.log('Création d\'une nouvelle actualité:', newNews);

        // Ajouter le document avec tous les champs requis
        await addDoc(collection(db, 'news'), newNews);
        setSuccess("Actualité créée avec succès!");

        // Réinitialiser le formulaire après création
        setTitle('');
        setContent('');
      }

      // Rediriger vers la page d'accueil après un court délai
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de l'actualité:", error);
      setError("Une erreur est survenue lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Paper sx={{ p: 4 }}>
        <ResponsiveTitle variant="h4" gutterBottom>
          {isEditMode ? "Modifier l'actualité" : "Créer une nouvelle actualité"}
        </ResponsiveTitle>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <ReactQuill
            theme="snow"
            value={title}
            onChange={setTitle}
            modules={titleModules}
            formats={titleFormats}
            placeholder="Titre de l'actualité"
            readOnly={saving}
          />

          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            modules={contentModules}
            formats={contentFormats}
            placeholder="Saisissez le contenu de votre actualité ici..."
            style={{ height: '300px', marginBottom: '1rem' }}
            readOnly={saving}
          />

          {/* Composant d'upload d'image */}
          <NewsImageUploader
            initialImageUrl={imageUrl}
            initialImagePath={imageStoragePath}
            initialImageFileName={imageFileName}
            onImageUploaded={(url, path, fileName) => {
              setImageUrl(url);
              setImageStoragePath(path);
              setImageFileName(fileName);
            }}
            onImageRemoved={() => {
              setImageUrl(null);
              setImageStoragePath(null);
              setImageFileName(null);
            }}
          />

          {/* Le slider "Publier immédiatement" a été supprimé */}

          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving || deleting}
                sx={{ minWidth: 120 }}
              >
                {saving ? <CircularProgress size={24} /> : isEditMode ? 'Mettre à jour' : 'Publier'}
              </Button>

              <Button
                variant="outlined"
                onClick={() => navigate('/')}
                disabled={saving || deleting}
              >
                Annuler
              </Button>
            </Box>

            {isEditMode && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setOpenDeleteDialog(true)}
                disabled={saving || deleting}
                sx={{ mt: { xs: 2, sm: 0 } }}
              >
                {deleting ? <CircularProgress size={24} /> : 'Supprimer'}
              </Button>
            )}
          </Box>

          {/* Dialog de confirmation de suppression */}
          <Dialog
            open={openDeleteDialog}
            onClose={() => {
              if (!deleting) {
                setOpenDeleteDialog(false);
                setDeleteConfirmation('');
              }
            }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 3 }}>
                Êtes-vous sûr de vouloir supprimer cette actualité ? Cette action est irréversible.
              </DialogContentText>

              <DialogContentText sx={{ mb: 2, fontWeight: 'bold' }}>
                Pour confirmer, veuillez écrire le mot "supprimer" ci-dessous :
              </DialogContentText>

              <TextField
                fullWidth
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="supprimer"
                variant="outlined"
                disabled={deleting}
                autoFocus
                error={deleteConfirmation !== '' && !deleteButtonEnabled}
                helperText={deleteConfirmation !== '' && !deleteButtonEnabled ?
                  'Veuillez écrire exactement "supprimer"' : ''}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setOpenDeleteDialog(false);
                  setDeleteConfirmation('');
                }}
                disabled={deleting}
              >
                Annuler
              </Button>
              <Button
                onClick={handleDelete}
                color="error"
                disabled={deleting || !deleteButtonEnabled}
                variant="contained"
              >
                {deleting ? <CircularProgress size={20} /> : 'Supprimer'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Paper>
    </Container>
  );
};

export default NewsEditor;
